import * as React from "react";
import { connect } from "react-redux";
import { RuleGroupsManager } from "components/admin/customer-user-groups/rule-groups-manager";
import { formatMessage } from "lib/globalize-util";
import Page from "components/page";
import { OrderedMap, fromJS } from "immutable";
import { updateGuestBuyerWhitelistProductValues } from "actions/brand";
import Toggle from "material-ui/Toggle";
import { fetchFilterValues, resetFilterValues } from "actions/filters/generic-filters";
import debounce from "lodash/debounce";
import type { IScope } from "reducers/scope";

export const DISPLAY_ALL_PRODUCTS_TO_GUEST_BUYER = {
    name: "guest buyer whitelist",
    product_filter_values: [{ field: "style_number", values: ["*"] }],
};

const PRODUCT_FILTER_VALUES = "product_filter_values";
const VALUES = "values";
// * Adds a `hide` prop to the group so that when the user toggles
// * 'Display all products with ___ values', the UI can check for
// * the `hide` prop and hide the values without discarding them
const addHideFlag = (group) => {
    return group.updateIn([PRODUCT_FILTER_VALUES], (rules) =>
        rules.map((rule) => {
            if (rule.get(VALUES).size === 1 && rule.get(VALUES).includes("*")) {
                rule = rule.set("hide", true);
            }
            return rule;
        })
    );
};
// * When the server sends back ['*'], it means that the user toggled
// * 'Display all products with ___ values' and saved it to the server.
// * This will set the values that are necessary, and server will remove
// * the `hide` prop for us
const addOrRemoveAsterisks = (groups) => {
    return groups.map((group) => {
        group = group.set(
            PRODUCT_FILTER_VALUES,
            group.get(PRODUCT_FILTER_VALUES).map((rule) => {
                return rule.has("hide") ? removeAsterisk(rule) : addAsterisk(rule);
            })
        );
        return group;
    });
};
// * If UI is not toggled for 'Display all products ...',
// * remove the asterisk before saving to server
const removeAsterisk = (rule) => {
    return rule.get(VALUES).size > 1 || !rule.get(VALUES).includes("*") ? rule.set(VALUES, ["*"]) : rule;
};
// * If UI is not toggled for 'Display all products ...',
// * add the asterisk before saving to server
const addAsterisk = (rule) => {
    return rule.get(VALUES).includes("*")
        ? rule.set(
              VALUES,
              rule.get(VALUES).filter((value) => value !== "*")
          )
        : rule;
};

interface IProps {
    scope: IScope;
    brandId: string;
    fields: any;
    filters: any;
    filterValues: any;
    groups: any;
    isUpdating: boolean;
    onFetchFilterValues: (url) => void;
    onResetFilterValues: () => void;
    onSave: (groups) => void;
}

interface IState {
    displayAllProducts: boolean;
    groups: any;
}

export class OnlineTradeShowPage extends React.PureComponent<IProps, IState> {
    muiStyles = {
        toggle: {
            width: "256px",
        },
    };
    constructor(props) {
        super(props);
        let groups = props.groups;
        const displayAllProducts =
            groups.hasIn([0, PRODUCT_FILTER_VALUES]) &&
            groups.getIn([0, PRODUCT_FILTER_VALUES]).size === 1 &&
            groups.getIn([0, PRODUCT_FILTER_VALUES, 0, "field"]) === "style_number" &&
            groups.getIn([0, PRODUCT_FILTER_VALUES, 0, VALUES]).includes("*");
        if (displayAllProducts) {
            groups = fromJS([
                {
                    _id: `_${Date.now()}`,
                    name: "guest buyer whitelist",
                    product_filter_values: [],
                },
            ]);
        } else {
            groups = groups.map(addHideFlag);
        }
        this.state = { displayAllProducts, groups };
    }
    componentDidUpdate(prevProps) {
        if (prevProps.isUpdating && !this.props.isUpdating) {
            this.setState({ groups: this.props.groups.map(addHideFlag) });
        }
    }
    handleSave = ({ groups }) => {
        if (this.state.displayAllProducts) {
            this.props.onSave([DISPLAY_ALL_PRODUCTS_TO_GUEST_BUYER]);
        } else {
            this.props.onSave(addOrRemoveAsterisks(groups).toJS());
        }
    };
    handleToggleDisplayAllProducts = () => {
        this.setState({ displayAllProducts: !this.state.displayAllProducts });
    };
    // tslint:disable-next-line
    handleFetchFilterValues = debounce((field, search) => {
        if (field) {
            const url = `/api/v3.0/filters/item/field/${field}/values?__brand.__ref=${this.props.brandId}&__search=${search}`;
            this.props.onFetchFilterValues(url);
        }
    }, 300);
    render() {
        const { scope, brandId, fields, filters, filterValues, isUpdating, onResetFilterValues } = this.props;
        const { displayAllProducts, groups } = this.state;
        const breadcrumbs = [
            {
                url: `/brand/${brandId}/admin/brand`,
                title: formatMessage("Brand Admin"),
            },
            {
                url: `/brand/${brandId}/admin/brand?activeTab=settings`,
                title: formatMessage("Settings"),
            },
        ];
        return (
            <Page
                breadcrumbs={breadcrumbs}
                openNavSection="admin"
                showTitle={true}
                title={formatMessage("Guest Buyer Visibility")}
            >
                <div style={{ fontSize: "14px" }}>
                    {formatMessage(
                        "Create and manage rules that determine which products are visible to buyers that are not yet connected to your brand."
                    )}
                    <br />
                    {formatMessage(
                        "No products are visible to guest buyers if no whitelist groups are created while allowing guest buyers to view your products."
                    )}
                    <br />
                    {formatMessage(
                        "When participating in Digital Trade Shows or Digital Fashion Weeks, display a “Shop this Brand” button to link your live event page to your storefront by displaying all or whitelisting products to guest buyers."
                    )}
                </div>
                <br />
                <Toggle
                    label={formatMessage("Display all products to buyers")}
                    onToggle={this.handleToggleDisplayAllProducts}
                    style={this.muiStyles.toggle}
                    toggled={displayAllProducts}
                />
                <RuleGroupsManager
                    scope={scope}
                    brandId={brandId}
                    canAddGroup={false}
                    canEditGroupName={false}
                    canRemoveGroup={false}
                    canToggleGroupType={false}
                    canToggleExcludeGroup={false}
                    canToggleRuleType={true}
                    displayAllProducts={displayAllProducts}
                    fields={fields}
                    filters={filters}
                    filterValues={filterValues}
                    groups={groups}
                    isUpdating={isUpdating}
                    onFetchFilterValues={this.handleFetchFilterValues}
                    onResetFilterValues={onResetFilterValues}
                    onSave={this.handleSave}
                />
            </Page>
        );
    }
}

const mapStateToProps = ({ app, entities, genericFilters, schemas, scope }) => {
    return {
        app,
        scope,
        brandId: scope && scope.brand && scope.brand._id,
        fields:
            (scope &&
                scope.brand &&
                schemas &&
                schemas.result &&
                schemas.result.length > 0 &&
                entities &&
                entities.schemas &&
                schemas.result.reduce((fields, schemaId) => {
                    if (
                        entities.schemas[schemaId] &&
                        entities.schemas[schemaId].brand === scope.brand._id &&
                        entities.schemas[schemaId].type === "product"
                    ) {
                        fields = fields.concat(
                            entities.schemas[schemaId].fields.filter(
                                (field) =>
                                    !field.details.value_dependency &&
                                    field.filterable &&
                                    !field.key.includes("sizes.") && // No size level fields
                                    field.details &&
                                    field.details.subtype !== "date"
                            )
                        );
                    }
                    return fields;
                }, [])[0]) || // Note: OrderedMap for some reason
            OrderedMap(),
        filters: genericFilters || {},
        filterValues: genericFilters.results.results || [],
        groups:
            (scope &&
                scope.brand &&
                scope.brand.settings &&
                scope.brand.settings.guest_buyer_product_whitelist_values &&
                fromJS(scope.brand.settings.guest_buyer_product_whitelist_values)) ||
            fromJS([]),
        isUpdating: scope.isUpdating,
    };
};

const mapDispatchToProps = (dispatch, state) => {
    return {
        onSave: (values) => dispatch(updateGuestBuyerWhitelistProductValues(values)),
        onFetchFilterValues: (url) => dispatch(fetchFilterValues(url)),
        onResetFilterValues: () => dispatch(resetFilterValues()),
    };
};

export const OnlineTradeShowContainer = connect(mapStateToProps, mapDispatchToProps)(OnlineTradeShowPage);
export default OnlineTradeShowContainer;
